import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import { CheckCircle, ContentCopy, RadioButtonUnchecked, VpnKey } from '@mui/icons-material';
import api from '../../../services/api';

/**
 * Subscrições (Mandatos) — ativar um comerciante para cobrança recorrente, como se
 * fez à Nebua. Mostra o que está pronto e o que falta, e cria a chave de API só com
 * as permissões de mandatos. A chave aparece UMA vez; depois só o prefixo.
 */
interface Estado {
  ecm: { esId: string; servico: string; tarifario: string | null; taxaPct: number; taxaPorOmissao: boolean }[];
  webhook: { url: string; ativo: boolean; temSegredo: boolean } | null;
  chaves: { id: string; nome: string; prefixo: string; estado: string }[];
}

const Passo: React.FC<{ ok: boolean; titulo: string; detalhe: React.ReactNode }> = ({ ok, titulo, detalhe }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    {ok ? <CheckCircle color="success" fontSize="small" sx={{ mt: '2px' }} /> : <RadioButtonUnchecked color="disabled" fontSize="small" sx={{ mt: '2px' }} />}
    <Box>
      <Typography variant="body2" fontWeight={600}>{titulo}</Typography>
      <Typography variant="caption" color="text.secondary" component="div">{detalhe}</Typography>
    </Box>
  </Stack>
);

const MandatosCard: React.FC<{ entityId: string }> = ({ entityId }) => {
  const [estado, setEstado] = useState<Estado | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aCriar, setACriar] = useState(false);
  const [novaChave, setNovaChave] = useState<string | null>(null);
  const [copiada, setCopiada] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const r = await api.get(`/admin/entities/${entityId}/mandatos`);
      setEstado(r.data?.data ?? null);
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Não foi possível ler o estado das subscrições.');
    }
  }, [entityId]);

  useEffect(() => { setNovaChave(null); setErro(null); carregar(); }, [carregar]);

  const criarChave = async () => {
    setACriar(true);
    setErro(null);
    try {
      const r = await api.post(`/admin/entities/${entityId}/mandatos/chave`);
      setNovaChave(r.data?.data?.apiKey ?? null);
      await carregar();
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Não foi possível criar a chave.');
    } finally {
      setACriar(false);
    }
  };

  const copiar = async () => {
    if (!novaChave) return;
    try { await navigator.clipboard.writeText(novaChave); setCopiada(true); } catch { /* sem permissão */ }
  };

  if (!estado) {
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>{erro ? <Alert severity="error">{erro}</Alert> : <CircularProgress size={20} />}</CardContent>
      </Card>
    );
  }

  const ecmOk = estado.ecm.length === 1;
  const chaveAtiva = estado.chaves.find((c) => c.estado === 'active');
  const linha = estado.ecm[0];

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>Subscrições (Mandatos)</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cobrança recorrente com o cartão Vinti4 guardado do cliente, até um valor máximo e uma vez por período, com autorização do cliente numa página Pagali.
        </Typography>

        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Passo
            ok={ecmOk}
            titulo="1. Serviço eCommerce ativo"
            detalhe={
              estado.ecm.length === 0 ? 'Falta — ative o eCommerce no separador Serviços (com o tarifário da comissão).'
                : estado.ecm.length > 1 ? 'Há mais de um eCommerce ativo — deixe só um, senão a taxa fica ambígua.'
                : <>{linha.servico} — comissão {linha.taxaPct}% + IVA{linha.taxaPorOmissao ? ' (por omissão: sem tarifário)' : ` (${linha.tarifario})`}</>
            }
          />
          <Passo
            ok={!!estado.webhook?.ativo && !!estado.webhook?.temSegredo}
            titulo="2. Webhook (recomendado)"
            detalhe={estado.webhook
              ? <>{estado.webhook.url}{estado.webhook.ativo ? '' : ' — inativo'}{estado.webhook.temSegredo ? '' : ' — sem segredo'}</>
              : 'Sem webhook — configure o URL do comerciante mais abaixo, nesta página. Sem ele o comerciante não é avisado de autorizações, cancelamentos e cobranças.'}
          />
          <Passo
            ok={!!chaveAtiva}
            titulo="3. Chave de API de mandatos"
            detalhe={chaveAtiva ? <>{chaveAtiva.nome} · {chaveAtiva.prefixo}</> : 'Por criar — só com as permissões mandatos:read, mandatos:write e mandatos:charge.'}
          />
        </Stack>

        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

        {novaChave && (
          <Alert severity="warning" sx={{ mb: 2 }} icon={<VpnKey />}>
            <Typography variant="body2" fontWeight={600}>Copie a chave agora — não volta a ser mostrada.</Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{novaChave}</Typography>
              <Tooltip title={copiada ? 'Copiada' : 'Copiar'}>
                <IconButton size="small" onClick={copiar}><ContentCopy fontSize="small" /></IconButton>
              </Tooltip>
            </Stack>
            <Typography variant="caption" component="div" sx={{ mt: 1 }}>
              Entregue-a ao comerciante por um canal seguro (não por email).
            </Typography>
          </Alert>
        )}

        {!chaveAtiva && (
          <Button variant="contained" startIcon={aCriar ? <CircularProgress size={16} color="inherit" /> : <VpnKey />}
            disabled={!ecmOk || aCriar} onClick={criarChave}>
            Criar chave de mandatos
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default MandatosCard;
